import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Input from '../../components/Input';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Skeleton } from '../../components/Skeleton';
import { ForumPostCard } from './components/ForumPostCard';
import { ForumPostForm } from './components/ForumPostForm';
import { ForumPost, ForumCategoria, ForumCategoriaLabels, CreateForumPostData } from '../../types/forum';
import { listForumPosts, getMyForumPosts, createForumPost } from '../../services/forumService';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/use-toast';

const PAGE_LIMIT = 30;

export function Forum() {
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [myPosts, setMyPosts] = useState<ForumPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState<ForumCategoria | 'all'>('all');
  const [orderBy, setOrderBy] = useState<'recentes' | 'mais_comentados' | 'mais_curtidos'>('recentes');

  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasPermission } = usePermissions();
  const { toast } = useToast();

  const canCreatePost = hasPermission('forum.criar_post');

  const loadPosts = useCallback(async () => {
    setIsLoading(true);
    setCurrentPage(1);
    try {
      const filters = {
        ...(categoriaFilter !== 'all' && { categoria: categoriaFilter }),
        ...(searchTerm && { search: searchTerm }),
        orderBy,
        page: 1,
        limit: PAGE_LIMIT,
      };
      const response = await listForumPosts(filters);
      setPosts(response.posts);
      setHasMore(response.hasMore);
    } catch {
      toast({ title: 'Erro', description: 'Falha ao carregar posts do fórum', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [categoriaFilter, searchTerm, orderBy, toast]);

  const loadMorePosts = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const filters = {
        ...(categoriaFilter !== 'all' && { categoria: categoriaFilter }),
        ...(searchTerm && { search: searchTerm }),
        orderBy,
        page: nextPage,
        limit: PAGE_LIMIT,
      };
      const response = await listForumPosts(filters);
      setPosts((prev) => [...prev, ...response.posts]);
      setHasMore(response.hasMore);
      setCurrentPage(nextPage);
    } catch {
      toast({ title: 'Erro', description: 'Falha ao carregar mais posts', variant: 'destructive' });
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, currentPage, categoriaFilter, searchTerm, orderBy, toast]);

  const loadMyPosts = useCallback(async () => {
    try {
      const data = await getMyForumPosts();
      setMyPosts(data);
    } catch {
      console.error('Erro ao carregar meus posts');
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    if (activeTab === 'meus') loadMyPosts();
  }, [activeTab, loadMyPosts]);

  // Scroll infinito via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLoading, loadMorePosts]);

  const handleCreatePost = async (data: CreateForumPostData) => {
    setIsCreating(true);
    try {
      await createForumPost(data);
      toast({ title: 'Sucesso', description: 'Post criado com sucesso!' });
      setShowCreateDialog(false);
      loadPosts();
      loadMyPosts();
    } catch (error: any) {
      toast({ title: 'Erro', description: error.response?.data?.message || 'Falha ao criar post', variant: 'destructive' });
    } finally {
      setIsCreating(false);
    }
  };

  const handlePostUpdated = (updatedPost: ForumPost) => {
    const update = (prev: ForumPost[]) => prev.map((p) => p.id === updatedPost.id ? updatedPost : p);
    setPosts(update);
    setMyPosts(update);
  };

  const handlePostDeleted = (postId: string) => {
    const filter = (prev: ForumPost[]) => prev.filter((p) => p.id !== postId);
    setPosts(filter);
    setMyPosts(filter);
  };

  const SkeletonList = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-3/4 mt-2" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3 mt-2" />
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const PostList = ({ list }: { list: ForumPost[] }) => (
    <div className="space-y-4">
      {list.map((post) => (
        <ForumPostCard
          key={post.id}
          post={post}
          onPostUpdated={handlePostUpdated}
          onPostDeleted={handlePostDeleted}
        />
      ))}
    </div>
  );

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Fórum</h1>
          <p className="text-muted-foreground mt-1">
            Compartilhe ideias, tire dúvidas e interaja com a comunidade
          </p>
        </div>
        {canCreatePost && (
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Post
          </Button>
        )}
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Select value={categoriaFilter} onValueChange={(value) => setCategoriaFilter(value as ForumCategoria | 'all')}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.values(ForumCategoria).map((cat) => (
                    <SelectItem key={cat} value={cat}>{ForumCategoriaLabels[cat]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={orderBy} onValueChange={(value) => setOrderBy(value as typeof orderBy)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recentes">Mais Recentes</SelectItem>
                  <SelectItem value="mais_comentados">Mais Comentados</SelectItem>
                  <SelectItem value="mais_curtidos">Mais Curtidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="todos">Todos os Posts</TabsTrigger>
          <TabsTrigger value="meus">Meus Posts</TabsTrigger>
        </TabsList>

        <TabsContent value="todos" className="mt-0">
          {isLoading ? (
            <SkeletonList />
          ) : posts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">
                  {searchTerm || categoriaFilter !== 'all'
                    ? 'Nenhum post encontrado com os filtros aplicados'
                    : 'Nenhum post no fórum ainda. Seja o primeiro a criar!'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <PostList list={posts} />
              <div ref={sentinelRef} className="h-4 mt-4" />
              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {!hasMore && posts.length >= PAGE_LIMIT && (
                <p className="text-center text-sm text-muted-foreground py-4">
                  Todos os posts foram carregados ({posts.length} no total)
                </p>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="meus" className="mt-0">
          {myPosts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">Você ainda não criou nenhum post.</p>
                {canCreatePost && (
                  <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Primeiro Post
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <PostList list={myPosts} />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Criar Novo Post</DialogTitle>
          </DialogHeader>
          <ForumPostForm
            onSubmit={handleCreatePost}
            onCancel={() => setShowCreateDialog(false)}
            isLoading={isCreating}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
